import { useState, type CSSProperties, type ReactNode } from 'react'
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type Announcements,
  type DragEndEvent,
} from '@dnd-kit/core'
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { Link, useNavigate } from 'react-router-dom'
import { withCountRu } from '@dorixona/utils'
import { Icon } from '../../ui/Icon'
import { Thumb } from '../../ui/Thumb'
import { Failed, failureText } from '../../ui/Load'
import { TableSkeleton } from '../../ui/Skeleton'
import { PageActions } from '../../app/PageHeader'
import { useSession } from '../auth/session'
import { useCategories, useSaveCategory, useSaveMany, type Category } from './api'
import { changedOrder, filterCategories, reorderCategories, sortedCategories } from './model'

export interface CategoriesScreenProps {
  onToast: (message: string) => void
}

/** Щелчок по полю или кнопке в строке не должен открывать категорию. */
const stay = (event: { stopPropagation: () => void }) => {
  event.stopPropagation()
}

/**
 * Что читалка говорит при перетаскивании: по-русски и названием категории,
 * а не «draggable item 3 was dropped over droppable area 5».
 */
function announcements(name: (id: unknown) => string): Announcements {
  return {
    onDragStart: ({ active }) => `Взяли «${name(active.id)}»`,
    onDragOver: ({ active, over }) =>
      over ? `«${name(active.id)}» над «${name(over.id)}»` : `«${name(active.id)}» вне списка`,
    onDragEnd: ({ active, over }) =>
      over ? `«${name(active.id)}» поставлена на место «${name(over.id)}»` : 'Порядок не изменён',
    onDragCancel: ({ active }) => `Перенос «${name(active.id)}» отменён`,
  }
}

const INSTRUCTIONS =
  'Чтобы поменять порядок, нажмите пробел, стрелками вверх и вниз выберите место и нажмите пробел ещё раз. Escape — отмена.'

interface SortableRowProps {
  category: Category
  href: string
  /** Перетаскивать можно: есть право и список не сужен поиском. */
  draggable: boolean
  /** Ручка видна, но выключена — объясняем почему. */
  handleHint: string | null
  onOpen: () => void
  children: ReactNode
}

/**
 * Строка, которую можно перетащить. Тянут за ручку слева, а не за всю строку:
 * по строке щёлкают, чтобы открыть категорию, и в ней есть переключатель.
 */
function SortableRow({
  category,
  href,
  draggable,
  handleHint,
  onOpen,
  children,
}: SortableRowProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: category.id,
    disabled: !draggable,
  })
  // Только по вертикали: строка таблицы не уезжает вбок.
  const style: CSSProperties = {
    transform: transform ? `translate3d(0, ${Math.round(transform.y)}px, 0)` : undefined,
    transition,
  }

  return (
    <tr
      ref={setNodeRef}
      style={style}
      data-href={href}
      data-dragging={isDragging || undefined}
      onClick={onOpen}
    >
      <td className="cat-grip" onClick={stay}>
        {handleHint === null ? null : (
          <button
            type="button"
            className="ui-iconbtn"
            aria-label={`Перетащить «${category.name}»`}
            data-tip={handleHint || undefined}
            disabled={!draggable}
            {...attributes}
            {...listeners}
          >
            <Icon name="i-grip" />
          </button>
        )}
      </td>
      {children}
    </tr>
  )
}

/**
 * Витрина приложения: список категорий в порядке показа. Порядок меняют
 * перетаскиванием, кэшбэк — в карточке категории: в списке он только виден.
 * Всё здесь ведёт админка — в учётной системе категорий нет.
 */
export function CategoriesScreen({ onToast }: CategoriesScreenProps) {
  const { can } = useSession()
  const navigate = useNavigate()
  const [q, setQ] = useState('')
  const categories = useCategories()
  const save = useSaveCategory()
  const saveMany = useSaveMany()
  // Счётчик «без категории» берём из каталога: сервер умеет отвечать на этот
  // вопрос одним числом, перебирать товары незачем.

  const canUpdate = can('categories', 'u')

  function update(category: Category, patch: Partial<Category>, message: string) {
    save.mutate(
      { ...category, ...patch },
      {
        onSuccess: () => {
          onToast(message)
        },
        onError: (error) => {
          onToast(failureText(error))
        },
      },
    )
  }

  // Пока порядок сохраняется, показываем уже новый: строка не прыгает назад
  // на время запроса. Ошибка — возвращаем серверный порядок.
  const [pending, setPending] = useState<Category[] | null>(null)
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  )

  const all = pending ?? sortedCategories(categories.data ?? [])
  const name = (id: unknown) => all.find((c) => c.id === id)?.name ?? ''

  function onDragEnd({ active, over }: DragEndEvent) {
    if (!over || active.id === over.id) return
    const next = sortedCategories(reorderCategories(all, Number(active.id), Number(over.id)))
    const moved = changedOrder(all, next)
    setPending(next)
    saveMany.mutate(moved, {
      onSuccess: () => {
        onToast('Порядок показа изменён')
      },
      onError: (error) => {
        onToast(failureText(error))
      },
      onSettled: () => {
        setPending(null)
      },
    })
  }

  const list = filterCategories(all, q)

  return (
    <>
      <PageActions>
        {categories.data ? (
          <span
            className="ui-count adm-count"
            aria-label={withCountRu(all.length, ['категория', 'категории', 'категорий'])}
          >
            {all.length}
          </span>
        ) : null}
        <span className="spacer" />
        {can('categories', 'c') ? (
          <button
            type="button"
            className="ui-btn"
            onClick={() => {
              void navigate('/categories/new')
            }}
          >
            <Icon name="i-plus" />
            Новая категория
          </button>
        ) : null}
      </PageActions>

      <div className="ui-toolbar" role="search">
        <div className="ui-search">
          <Icon name="i-search" />
          <input
            className="ui-input"
            type="search"
            placeholder="Название категории"
            aria-label="Поиск категории"
            value={q}
            onChange={(e) => {
              setQ(e.target.value)
            }}
          />
        </div>
      </div>

      <section className="ui-table-card" aria-label="Категории">
        {categories.isPending ? <TableSkeleton what="категории" /> : null}
        {categories.isError ? (
          <Failed
            what="категории"
            error={categories.error}
            onRetry={() => {
              void categories.refetch()
            }}
          />
        ) : null}

        {categories.data ? (
          <div className="ui-table-wrap">
            <table className="ui-table ui-categories">
              <colgroup>
                <col className="w-grip" />
                <col />
                <col className="w-cb" />
                <col className="w-count" />
                <col className="w-on" />
              </colgroup>
              <thead>
                <tr>
                  <th scope="col">
                    <span className="visually-hidden">Порядок</span>
                  </th>
                  <th scope="col">Категория</th>
                  <th scope="col" data-align="end">
                    Кэшбэк
                  </th>
                  <th scope="col" data-align="end">
                    Товаров
                  </th>
                  <th scope="col" data-align="center">
                    Витрина
                  </th>
                </tr>
              </thead>
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={onDragEnd}
                accessibility={{
                  announcements: announcements(name),
                  screenReaderInstructions: { draggable: INSTRUCTIONS },
                }}
              >
                <SortableContext
                  items={list.map((c) => c.id)}
                  strategy={verticalListSortingStrategy}
                >
                  <tbody>
                    {list.length === 0 ? (
                      <tr>
                        <td colSpan={5}>
                          {q ? (
                            <div className="ui-empty">
                              <Icon name="i-search" />
                              <b>Категории не найдены</b>
                              <p>Измените запрос — ищем по русскому и узбекскому названию.</p>
                              <button
                                type="button"
                                className="ui-btn"
                                data-size="sm"
                                data-variant="secondary"
                                onClick={() => {
                                  setQ('')
                                }}
                              >
                                Сбросить поиск
                              </button>
                            </div>
                          ) : (
                            <div className="ui-empty">
                              <Icon name="i-tag" />
                              <b>Категорий пока нет</b>
                            </div>
                          )}
                        </td>
                      </tr>
                    ) : null}
                    {list.map((category) => {
                      const href = `/categories/${category.id}`
                      return (
                        <SortableRow
                          key={category.id}
                          category={category}
                          href={href}
                          draggable={canUpdate && !q && !saveMany.isPending}
                          // Без права ручки нет; во время поиска она видна, но объясняет,
                          // почему не тянется: место в сужённом списке ничего не говорит.
                          handleHint={
                            canUpdate ? (q ? 'Сбросьте поиск, чтобы менять порядок' : '') : null
                          }
                          onOpen={() => {
                            void navigate(href)
                          }}
                        >
                          <td>
                            <div className="ui-product">
                              {/* Адрес обложки приходит готовым; без обложки —
                                  нейтральная заглушка, как у товара без фото. */}
                              <Thumb
                                photos={category.coverUrl ? [category.coverUrl] : []}
                                size={40}
                                alt={category.name}
                              />
                              <div className="txt">
                                <Link to={href} className="primary" onClick={stay}>
                                  {category.name}
                                </Link>
                                <span className="secondary">
                                  {category.nameUz || (
                                    <span className="ui-tag" data-tone="warning">
                                      нет названия на узбекском
                                    </span>
                                  )}
                                </span>
                              </div>
                            </div>
                          </td>
                          <td data-align="end" className="num">
                            {category.cb}%
                          </td>
                          <td data-align="end">{category.products}</td>
                          <td data-align="center" onClick={stay}>
                            {canUpdate ? (
                              <label className="ui-switch" data-size="sm">
                                <input
                                  type="checkbox"
                                  role="switch"
                                  checked={category.active}
                                  aria-label={`Показывать «${category.name}» в приложении`}
                                  onChange={(e) => {
                                    update(
                                      category,
                                      { active: e.target.checked },
                                      e.target.checked
                                        ? 'Категория показана в приложении'
                                        : 'Категория скрыта',
                                    )
                                  }}
                                />
                              </label>
                            ) : category.active ? (
                              <span className="ui-status" data-tone="success">
                                показана
                              </span>
                            ) : (
                              <span className="ui-status">скрыта</span>
                            )}
                          </td>
                        </SortableRow>
                      )
                    })}
                  </tbody>
                </SortableContext>
              </DndContext>
            </table>
          </div>
        ) : null}
      </section>
    </>
  )
}
